import {
  ASSET_STATUS_LABELS,
  AssetStatus,
  DashboardAssetExpiringKind,
  type AuthUser,
  type DashboardAssetStatusCount,
  type DashboardAssetTypeCount,
  type DashboardAssetsSummary,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { assetListPath } from '@/products/helpdesk/features/assets/asset-list-path';
import {
  DashboardPanel,
  DashboardPanelLinkAction,
} from '@/products/helpdesk/features/dashboard/dashboard-panel';
import { useDashboardSummary } from '@/products/helpdesk/features/dashboard/use-dashboard-summary';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

const STATUS_BAR_COLORS: Record<AssetStatus, string> = {
  [AssetStatus.IN_SERVICE]: 'bg-brand-navy',
  [AssetStatus.IN_STOCK]: 'bg-emerald-600/80',
  [AssetStatus.DEFECTIVE]: 'bg-amber-500',
  [AssetStatus.DECOMMISSIONED]: 'bg-muted-foreground/50',
};

export function AssetsDashboardPanel({ user }: { user: AuthUser }) {
  void user;
  const summary = useDashboardSummary({ enabled: true });

  return (
    <DashboardPanel
      slotId="assets-licenses"
      title="Lizenzen & Assets"
      description="Bestand und Ablauf aus dem Inventar."
      action={<DashboardPanelLinkAction to="/helpdesk/assets" label="Inventar" />}
    >
      {summary.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Inventar wird geladen …
        </p>
      ) : null}

      {summary.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {summary.error instanceof ApiRequestError
            ? summary.error.message
            : 'Asset-Kennzahlen konnten nicht geladen werden.'}
        </p>
      ) : null}

      {summary.data?.assets ? <AssetsBody assets={summary.data.assets} /> : null}

      {summary.data && !summary.data.assets && !summary.isPending && !summary.isError ? (
        <p className="text-sm text-muted-foreground">Keine Asset-Daten verfügbar.</p>
      ) : null}
    </DashboardPanel>
  );
}

function AssetsBody({ assets }: { assets: DashboardAssetsSummary }) {
  const software = assets.countsByType.find((row) => row.slug === 'software');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <Kpi
          label="Software-Lizenzen"
          value={assets.licenceCount}
          href={software ? assetListPath({ assetTypeId: software.typeId }) : '/helpdesk/assets'}
        />
        <Kpi
          label="Defekt"
          value={
            assets.countsByStatus.find((row) => row.status === AssetStatus.DEFECTIVE)?.count ?? 0
          }
          href={assetListPath({ status: AssetStatus.DEFECTIVE })}
          tone="warn"
        />
      </div>

      <StatusStack counts={assets.countsByStatus} />

      {assets.countsByType.length > 0 ? (
        <div>
          <p className="mb-1.5 text-xs font-medium tracking-wide uppercase">Nach Typ</p>
          <ul className="space-y-1">
            {assets.countsByType.map((row) => (
              <TypeRow key={row.typeId} row={row} />
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <p className="mb-1.5 text-xs font-medium tracking-wide uppercase">
          Ablauf Garantie / Wartung (90 Tage)
        </p>
        {assets.expiringSoon.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Keine ablaufenden Termine in den nächsten 90 Tagen.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border">
            {assets.expiringSoon.map((item) => (
              <li key={`${item.id}-${item.kind}`}>
                <Link
                  to={`/helpdesk/assets/${item.id}`}
                  className="flex flex-col gap-0.5 px-3 py-2 hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="min-w-0 truncate text-sm font-medium">
                    {item.name}
                    {item.inventoryTag ? (
                      <span className="font-normal text-muted-foreground">
                        {' '}
                        · {item.inventoryTag}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      item.urgent
                        ? 'font-medium text-amber-800 dark:text-amber-200'
                        : 'text-muted-foreground',
                    )}
                  >
                    {item.kind === DashboardAssetExpiringKind.WARRANTY ? 'Garantie' : 'Wartung'}{' '}
                    {formatDeDate(item.until)}
                    {item.urgent ? ' · ≤30 Tage' : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusStack({ counts }: { counts: readonly DashboardAssetStatusCount[] }) {
  const total = counts.reduce((sum, row) => sum + row.count, 0);
  if (total === 0) {
    return <p className="text-sm text-muted-foreground">Noch keine Assets erfasst.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium tracking-wide uppercase">Bestand nach Status</p>
      <div
        className="flex h-3 overflow-hidden rounded-full"
        role="img"
        aria-label="Verteilung nach Asset-Status"
      >
        {counts.map((row) => (
          <Link
            key={row.status}
            to={assetListPath({ status: row.status })}
            title={`${row.label}: ${row.count}`}
            className={cn(
              STATUS_BAR_COLORS[row.status],
              'min-w-[2px] transition-opacity hover:opacity-90',
            )}
            style={{ width: `${(row.count / total) * 100}%` }}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {counts.map((row) => (
          <li key={row.status} className="inline-flex items-center gap-1.5">
            <span className={cn('size-2 rounded-sm', STATUS_BAR_COLORS[row.status])} aria-hidden />
            <Link
              to={assetListPath({ status: row.status })}
              className="underline-offset-2 hover:underline"
            >
              {ASSET_STATUS_LABELS[row.status]} ({row.count})
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TypeRow({ row }: { row: DashboardAssetTypeCount }) {
  return (
    <li className="flex items-center justify-between gap-2 text-sm">
      <Link
        to={assetListPath({ assetTypeId: row.typeId })}
        className="truncate underline-offset-2 hover:text-primary hover:underline"
      >
        {row.name}
      </Link>
      <span className="tabular-nums">{row.count}</span>
    </li>
  );
}

function Kpi({
  label,
  value,
  href,
  tone = 'default',
}: {
  label: string;
  value: number;
  href: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <Link
      to={href}
      className={cn(
        'rounded-md border px-2.5 py-2 transition-colors hover:bg-muted/60',
        tone === 'warn' && value > 0 && 'border-amber-300/80 bg-amber-50/80 dark:bg-amber-950/30',
      )}
    >
      <p className="text-[11px] leading-tight text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tracking-tight tabular-nums">{value}</p>
    </Link>
  );
}

function formatDeDate(ymd: string): string {
  const [year, month, day] = ymd.split('-');
  if (!year || !month || !day) {
    return ymd;
  }
  return `${day}.${month}.${year}`;
}
